import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import {
    fetchIntegrationLogs,
    downloadIntegrationLogCsv,
} from '../../../../../../jsx/actions/settingsAction';
import ProspectProblemsBadge, { totalProblems } from './ProspectProblemsBadge';

const INTEGRATION_TYPE = 'prospect';

const TRIGGER_LABELS = {
    test: 'Test',
    manual: 'Manuel',
    ticker: 'Automatique',
    cli: 'CLI',
};

// Mêmes couleurs que le résultat par catégorie du test / de la synchro.
const STATUS_META = {
    ok: { color: '#2e7d32', label: 'Importé' },
    demo: { color: '#1565c0', label: 'Démo' },
    // Couleur distincte de Démo : aucun appel réseau n'a eu lieu (httpCode 0).
    simulation: { color: '#455a64', label: 'Simulation' },
    partial: { color: '#f57f17', label: 'Partiel' },
    error: { color: '#c62828', label: 'Erreur' },
};

const MESSAGE_MAX_LENGTH = 60;

function formatDate(at) {
    if (!at) return '';
    // `at` peut porter jusqu'à 9 décimales de secondes (RFC3339 domainModel) ; Date ne parse
    // fiablement que les millisecondes (3 décimales), on tronque avant de parser.
    const truncated = at.replace(/(\.\d{3})\d+/, '$1');
    const date = new Date(truncated);
    return isNaN(date.getTime()) ? at : date.toLocaleString('fr-FR');
}

/**
 * Journal des envois Prospect : une ligne par paquet envoyé (test, synchro manuelle ou
 * automatique, CLI), avec le CSV des enregistrements envoyés téléchargeable.
 * Masqué si domainModel refuse l'accès (403/404, même règle que la colonne Home).
 * Le parent incrémente `reloadKey` après un test et à la fin d'une synchro pour recharger.
 */
class ProspectPushLogTable extends Component {
    state = {
        // État du téléchargement par ligne : { [logId]: { loading, error } }
        downloads: {},
    };

    componentDidMount() {
        this.props.fetchIntegrationLogs(INTEGRATION_TYPE);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.reloadKey !== this.props.reloadKey) {
            this.handleRefresh();
        }
    }

    handleRefresh = () => {
        this.props.fetchIntegrationLogs(INTEGRATION_TYPE);
    };

    handleOlder = () => {
        const { prospect } = this.props;
        this.props.fetchIntegrationLogs(INTEGRATION_TYPE, { before: prospect.logsNextBefore, append: true });
    };

    setDownload(logId, patch) {
        this.setState((prevState) => ({
            downloads: { ...prevState.downloads, [logId]: patch },
        }));
    }

    handleDownload = (log) => () => {
        this.setDownload(log.id, { loading: true, error: null });
        downloadIntegrationLogCsv(INTEGRATION_TYPE, log).then((result) => {
            this.setDownload(log.id, { loading: false, error: result.ok ? null : result.error });
        });
    };

    renderStatus(status) {
        const meta = STATUS_META[status] || { color: '#616161', label: status || 'Inconnu' };
        return (
            <Chip
                size="small"
                label={meta.label}
                style={{ backgroundColor: meta.color, color: '#fff' }}
            />
        );
    }

    renderMessage(message) {
        if (!message) return '';
        if (message.length <= MESSAGE_MAX_LENGTH) return message;
        return (
            <span title={message}>{message.slice(0, MESSAGE_MAX_LENGTH)}…</span>
        );
    }

    renderRow(log) {
        const { categoryLabels } = this.props;
        const download = this.state.downloads[log.id] || {};
        const importIds = Array.isArray(log.importIds) ? log.importIds.join(', ') : '';

        return (
            <TableRow key={log.id}>
                <TableCell style={{ whiteSpace: 'nowrap' }}>{formatDate(log.at)}</TableCell>
                <TableCell>{TRIGGER_LABELS[log.trigger] || log.trigger || ''}</TableCell>
                <TableCell>{log.triggeredBy || '—'}</TableCell>
                <TableCell>{(categoryLabels && categoryLabels[log.category]) || log.category || ''}</TableCell>
                <TableCell>{this.renderStatus(log.status)}</TableCell>
                <TableCell>{log.httpCode || ''}</TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>
                    {typeof log.sent === 'number' ? log.sent : '-'} / {typeof log.recordCount === 'number' ? log.recordCount : '-'}
                </TableCell>
                <TableCell>{importIds}</TableCell>
                <TableCell>{log.demo ? 'Oui' : 'Non'}</TableCell>
                <TableCell>{this.renderMessage(log.message)}</TableCell>
                <TableCell>
                    {/* « — » pour un journal sans manque (ou antérieur au contrôle BGFA). */}
                    {totalProblems(log.problems) > 0 ? (
                        <ProspectProblemsBadge problems={log.problems} onClick={this.props.onProblemsClick} />
                    ) : (
                        '—'
                    )}
                </TableCell>
                <TableCell>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={this.handleDownload(log)}
                        disabled={!!download.loading}
                        startIcon={download.loading ? <CircularProgress size={14} /> : null}
                    >
                        CSV
                    </Button>
                    {download.error && (
                        <Typography variant="caption" color="error" component="div">
                            {download.error}
                        </Typography>
                    )}
                </TableCell>
            </TableRow>
        );
    }

    render() {
        const { prospect } = this.props;
        if (!prospect || prospect.logsForbidden) return null;

        const logs = prospect.logs || [];
        const { logsLoading, logsError, logsNextBefore } = prospect;

        return (
            <Box p={1}>
                <Divider style={{ marginBottom: 12 }} />
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2">Journal des envois</Typography>
                    <Button
                        size="small"
                        onClick={this.handleRefresh}
                        disabled={logsLoading}
                        startIcon={logsLoading ? <CircularProgress size={14} /> : null}
                    >
                        Actualiser
                    </Button>
                </Box>

                {logsError && (
                    <Typography variant="body2" color="error">{logsError}</Typography>
                )}

                {logs.length === 0 ? (
                    !logsLoading && !logsError && (
                        <Typography variant="body2" color="textSecondary">Aucun envoi enregistré.</Typography>
                    )
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Déclencheur</TableCell>
                                    <TableCell>Par</TableCell>
                                    <TableCell>Catégorie</TableCell>
                                    <TableCell>Statut</TableCell>
                                    <TableCell>HTTP</TableCell>
                                    <TableCell>Envoyés / Tentés</TableCell>
                                    <TableCell>Import IDs</TableCell>
                                    <TableCell>Démo</TableCell>
                                    <TableCell>Message</TableCell>
                                    <TableCell>Données BGFA</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map((log) => this.renderRow(log))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {logs.length > 0 && (
                    <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 4 }}>
                        Dans le CSV d'une entrée, la colonne manques_bgfa est recalculée avec les règles actuelles.
                    </Typography>
                )}

                {logsNextBefore && (
                    <Box mt={1}>
                        <Button size="small" variant="outlined" onClick={this.handleOlder} disabled={logsLoading}>
                            Plus ancien
                        </Button>
                    </Box>
                )}
            </Box>
        );
    }
}

function mapStateToProps(state) {
    return {
        prospect: state.settings && state.settings.integrations && state.settings.integrations[INTEGRATION_TYPE],
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({ fetchIntegrationLogs }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ProspectPushLogTable);
